// ThresholdBand — the tolerance-band gauge (ME-CARDS-R1 A5, arm-model.PNG).
//
// The design centerpiece for the ONLY figure that carries a `threshold`: a
// horizontal band with the low/target/high scale, an in-band fill, and a marker
// at the figure's value.
//
// Load-bearing honesty (geometry resolved in the engine): a null / non-numeric
// value, or one outside the drawn range, gets NO marker and NO inside/outside
// caption — the band still renders because the band IS payload data, but we
// never imply a position we cannot honestly draw.
import React from "react";

import type { Figure } from "../../../chatBot/models/machineEvidenceModels";
import { resolveThresholdBand } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { VERDICT_TONE_COLORS } from "./honestyTokens";

export interface ThresholdBandProps {
  figure: Figure;
  /** dim with the rest of the content when the card feed is DARK. */
  dim?: boolean;
}

const num = (v: number): string => v.toLocaleString();

export const ThresholdBand: React.FC<ThresholdBandProps> = ({ figure, dim }) => {
  const { translate } = useTranslation("machineEvidence");
  const band = resolveThresholdBand(figure);
  if (!band) return null;

  const unit = figure.unit ? ` ${figure.unit}` : "";
  const inside = band.state === "inside";
  const tone = VERDICT_TONE_COLORS[inside ? "positive" : "neutral"];
  const toleranceText =
    band.symmetricTolerance !== null
      ? `±${num(band.symmetricTolerance)}${unit}`
      : `${num(band.low)}${unit} – ${num(band.high)}${unit}`;

  const caption =
    band.state === null
      ? null
      : (translate(inside ? "threshold.insideBand" : "threshold.outsideBand", {
          tolerance: toleranceText,
          defaultValue: inside ? "inside the {{tolerance}} band" : "outside the {{tolerance}} band",
        }) as string);

  return (
    <div
      className="msc-threshold-band"
      data-band-state={band.state ?? "unknown"}
      style={{ display: "flex", flexDirection: "column", gap: 4, opacity: dim ? 0.55 : 1 }}
    >
      <div
        style={{
          position: "relative",
          height: 14,
          borderRadius: dt.radiusPill,
          background: dt.inputBg,
          border: `1px solid ${dt.cardBorder}`,
        }}
      >
        {/* in-band fill */}
        <div
          data-band-fill
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${band.bandStartPct}%`,
            width: `${band.bandEndPct - band.bandStartPct}%`,
            background: tone.bg,
            borderRadius: dt.radiusPill,
          }}
        />
        {/* target hairline */}
        {band.targetPct !== null ? (
          <div
            data-band-target
            style={{
              position: "absolute",
              top: 2,
              bottom: 2,
              left: `${band.targetPct}%`,
              width: 1,
              background: dt.textMuted,
            }}
          />
        ) : null}
        {/* value marker — omitted entirely when it cannot be drawn honestly */}
        {band.markerPct !== null ? (
          <div
            data-band-marker
            title={`${num(figure.value as number)}${unit}`}
            style={{
              position: "absolute",
              top: -2,
              bottom: -2,
              left: `${band.markerPct}%`,
              width: 2,
              marginLeft: -1,
              borderRadius: 1,
              background: dt.textPrimary,
            }}
          />
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: dt.fontMono,
          fontSize: 10,
          color: dt.textMuted,
        }}
      >
        <span>{`${num(band.low)}${unit}`}</span>
        {band.target !== null ? (
          <span>
            {translate("threshold.target", {
              value: num(band.target),
              defaultValue: "target {{value}}",
            }) as string}
          </span>
        ) : null}
        <span>{`${band.high > 0 ? "+" : ""}${num(band.high)}${unit}`}</span>
      </div>

      {caption ? (
        <span style={{ fontSize: 11, color: dt.textSecondary, fontStyle: "italic" }}>{caption}</span>
      ) : null}
    </div>
  );
};

export default ThresholdBand;
